/**
 * HoldOn Discount Popup
 * Handles discount signup popups with Klaviyo integration
 */

class HoldOnDiscountPopup {
  constructor() {
    this.popup = document.querySelector('[data-popup-modal]');
    this.form = document.querySelector('[data-discount-form]');
    this.currentDiscountType = null;
    
    if (this.popup && this.form) {
      this.init();
    }
  }

  init() {
    this.setupTriggers();
    this.setupCloseButtons();
    this.setupFormSubmission();
    this.setupWhatsAppToggle();
    this.preventBackgroundScroll();
    this.setupAutoShow();
  }

  setupAutoShow() {
    // Check if popup has already been shown in this session
    const popupShownKey = 'holdon-discount-popup-shown';
    const hasBeenShown = sessionStorage.getItem(popupShownKey) === 'true';
    
    if (hasBeenShown) {
      return;
    }
    
    // Show popup after 2 seconds delay
    setTimeout(() => {
      this.openPopup('25'); // Show 25% discount popup
    }, 2000);
  }

  setupTriggers() {
    // Listen for clicks on discount signup triggers
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-discount-trigger]');
      if (trigger) {
        e.preventDefault();
        const discountType = trigger.getAttribute('data-discount-type');
        this.openPopup(discountType);
      }
    });
  }

  openPopup(discountType = '40') {
    this.currentDiscountType = discountType;
    this.updatePopupContent(discountType);
    
    // Show popup
    this.popup.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    
    // Add fade-in animation
    requestAnimationFrame(() => {
      this.popup.classList.add('is-visible');
    });
    
    // Reset form to first step
    this.showStep('form');
  }

  closePopup() {
    this.popup.classList.remove('is-visible');
    
    // Mark as shown when closed
    sessionStorage.setItem('holdon-discount-popup-shown', 'true');
    
    setTimeout(() => {
      this.popup.style.display = 'none';
      document.body.style.overflow = '';
      this.resetForm();
    }, 300);
  }

  updatePopupContent(discountType) {
    const title = this.popup.querySelector('[data-popup-title]');
    const benefitText = this.popup.querySelector('[data-benefit-text]');
    const bundleImage = this.popup.querySelector('[data-image-bundle]');
    const singleImage = this.popup.querySelector('[data-image-single]');

    if (discountType === '25') {
      title.textContent = "Enter your best email below to secure your 25% founder's discount. We'll notify you the second we launch.";
      benefitText.innerHTML = '<span style="font-weight: 600;">Get 25% discount</span> on your order';
      
      // Show single product image
      if (bundleImage) bundleImage.style.display = 'none';
      if (singleImage) singleImage.style.display = 'block';
    } else {
      title.textContent = "Enter your best email below to secure your 40% Bundlediscount. We'll notify you the second we launch.";
      benefitText.innerHTML = '<span style="font-weight: 600;">Get 40% discount</span> on your bundle';
      
      // Show bundle image
      if (bundleImage) bundleImage.style.display = 'block';
      if (singleImage) singleImage.style.display = 'none';
    }
  }

  setupCloseButtons() {
    const closeButtons = this.popup.querySelectorAll('[data-popup-close]');
    closeButtons.forEach(button => {
      button.addEventListener('click', () => this.closePopup());
    });

    // Close on outside click
    this.popup.addEventListener('click', (e) => {
      if (e.target === this.popup) {
        this.closePopup();
      }
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.popup.classList.contains('is-visible')) {
        this.closePopup();
      }
    });
  }

  setupWhatsAppToggle() {
    const checkbox = this.popup.querySelector('[data-whatsapp-checkbox]');
    const phoneWrapper = this.popup.querySelector('[data-phone-wrapper]');
    
    if (checkbox && phoneWrapper) {
      checkbox.addEventListener('change', () => {
        if (checkbox.checked) {
          phoneWrapper.classList.add('is-visible');
        } else {
          phoneWrapper.classList.remove('is-visible');
        }
      });
    }
  }

  setupFormSubmission() {
    this.form.addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.handleSubmit();
    });
  }

  async handleSubmit() {
    const submitButton = this.form.querySelector('button[type="submit"]');
    const submitText = submitButton.querySelector('.holdon-discount-popup__submit-text');
    const submitLoading = submitButton.querySelector('.holdon-discount-popup__submit-loading');
    const errorDiv = this.form.querySelector('.holdon-discount-popup__error');

    // Get form data
    const formData = new FormData(this.form);
    const email = formData.get('email');
    const phone = formData.get('phone') || '';
    const whatsappOptIn = formData.get('whatsapp_opt_in') === 'yes';

    // Validate email
    if (!this.isValidEmail(email)) {
      this.showError('Please enter a valid email address');
      return;
    }

    // Show loading state
    submitButton.disabled = true;
    submitText.style.display = 'none';
    submitLoading.style.display = 'block';
    errorDiv.style.display = 'none';

    // Initialize _learnq if not exists
    window._learnq = window._learnq || [];
    
    // Build the event properties
    const eventProperties = {
      'discount_type': this.currentDiscountType + '%',
      'signup_source': 'discount_popup',
      'whatsapp_opt_in': whatsappOptIn ? 'yes' : 'no',
      'email': email
    };
    
    if (phone) {
      eventProperties['phone'] = phone;
    }
    
    // Track the event (captures custom properties)
    window._learnq.push(['track', 'Discount Popup Signup', eventProperties]);
    
    // Identify the profile with custom properties
    const profileData = {
      '$email': email,
      'Discount Type': this.currentDiscountType + '%',
      'Signup Source': 'discount_popup',
      'WhatsApp Opt In': whatsappOptIn ? 'yes' : 'no'
    };
    
    if (phone) {
      profileData['$phone_number'] = phone;
    }
    
    window._learnq.push(['identify', profileData]);
    
    // Subscribe to list using Klaviyo's client-side subscriptions API
    try {
      await this.subscribeToKlaviyoList(email, phone, whatsappOptIn);
    } catch (error) {
      console.error('Failed to subscribe to list:', error);
      // Continue anyway - profile is created
    }
    
    // Show success
    this.showStep('success');
  }

  async subscribeToKlaviyoList(email, phone, whatsappOptIn) {
    const apiKey = this.getKlaviyoApiKey();
    const listId = this.getKlaviyoListId();
    
    if (!apiKey || !listId) {
      console.warn('Klaviyo API key or list ID not configured');
      return false;
    }
    
    // Try using Klaviyo's embedded form endpoint (most reliable for client-side)
    const formData = new URLSearchParams();
    formData.append('g', listId);
    formData.append('email', email);
    
    if (phone) {
      formData.append('$fields', '$phone_number');
      formData.append('$phone_number', phone);
    }
    
    // Add custom properties
    formData.append('$fields', '$discount_type,$signup_source,$whatsapp_opt_in');
    formData.append('$discount_type', this.currentDiscountType + '%');
    formData.append('$signup_source', 'discount_popup');
    formData.append('$whatsapp_opt_in', whatsappOptIn ? 'yes' : 'no');
    
    try {
      const response = await fetch(`https://manage.kmail-lists.com/ajax/subscriptions/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString()
      });
      
      const responseText = await response.text();
      
      if (!response.ok) {
        console.error('Klaviyo subscription failed:', response.status, responseText);
        throw new Error('Subscription failed');
      }
      
      // Try parsing response
      try {
        const data = JSON.parse(responseText);
        if (data.success === false || data.errors) {
          console.error('Klaviyo subscription error:', data);
          throw new Error(data.errors || 'Subscription failed');
        }
      } catch (e) {
        // Response might not be JSON, that's ok if status was 200
        if (response.ok) {
          return true;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error subscribing to Klaviyo:', error);
      throw error;
    }
  }

  getKlaviyoListId() {
    const settingsScript = document.querySelector('[data-klaviyo-settings]');
    if (settingsScript) {
      try {
        const settings = JSON.parse(settingsScript.textContent);
        return settings.list_id;
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  getKlaviyoApiKey() {
    // Try to get from section settings
    const settingsScript = document.querySelector('[data-klaviyo-settings]');
    if (settingsScript) {
      try {
        const settings = JSON.parse(settingsScript.textContent);
        return settings.api_key;
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  showStep(step) {
    const formStep = this.popup.querySelector('[data-step="form"]');
    const successStep = this.popup.querySelector('[data-step="success"]');

    if (step === 'form') {
      formStep.style.display = 'block';
      successStep.style.display = 'none';
    } else {
      formStep.style.display = 'none';
      successStep.style.display = 'block';
    }
  }

  showError(message) {
    const errorDiv = this.form.querySelector('.holdon-discount-popup__error');
    errorDiv.textContent = message;
    errorDiv.style.display = 'block';

    setTimeout(() => {
      errorDiv.style.display = 'none';
    }, 5000);
  }

  resetForm() {
    this.form.reset();
    const submitButton = this.form.querySelector('button[type="submit"]');
    const submitText = submitButton.querySelector('.holdon-discount-popup__submit-text');
    const submitLoading = submitButton.querySelector('.holdon-discount-popup__submit-loading');
    const phoneWrapper = this.popup.querySelector('[data-phone-wrapper]');
    
    submitButton.disabled = false;
    submitText.style.display = 'block';
    submitLoading.style.display = 'none';
    
    // Hide phone wrapper on reset
    if (phoneWrapper) {
      phoneWrapper.classList.remove('is-visible');
    }
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  preventBackgroundScroll() {
    // Prevent scroll on background when popup is open
    const overlay = this.popup.querySelector('.holdon-discount-popup__overlay');
    if (overlay) {
      overlay.addEventListener('touchmove', (e) => {
        e.preventDefault();
      }, { passive: false });
    }
  }

  // Public method to reset the popup (for testing/debugging)
  reset() {
    sessionStorage.removeItem('holdon-discount-popup-shown');
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.holdOnDiscountPopup = new HoldOnDiscountPopup();
  });
} else {
  window.holdOnDiscountPopup = new HoldOnDiscountPopup();
}

// Export for external use
window.HoldOnDiscountPopup = HoldOnDiscountPopup;
